import React, { useState } from 'react';
import {
  Container, Typography, Box, Button, TextField, Dialog,
  DialogTitle, DialogContent, DialogActions, Snackbar, Alert,
  Paper, Grid, Card, CardContent, IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SendIcon from '@mui/icons-material/Send';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import customTheme from '../../theme';

const GradientHero = styled(Box)({
  background: `linear-gradient(135deg, ${customTheme.colors.secondary[300]} 0%, ${customTheme.colors.secondary[500]} 100%)`,
  color: customTheme.colors.secondary[900],
  py: 6,
  borderRadius: customTheme.borderRadius.lg,
  mb: 4,
  textAlign: 'center',
  position: 'relative',
});

const ContactCard = styled(Card)({
  height: '100%',
  transition: 'transform 0.3s ease',
  border: `1px solid ${customTheme.colors.secondary[100]}`,
  '&:hover': {
    transform: 'translateY(-4px)',
  },
});

export default function Contact() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = t('contact_page.form.name_required');
    if (!formData.email.trim()) {
      newErrors.email = t('contact_page.form.email_required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('contact_page.form.email_invalid');
    }
    if (!formData.phone.trim()) newErrors.phone = t('contact_page.form.phone_required');
    if (!formData.message.trim()) newErrors.message = t('contact_page.form.message_required');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const response = await fetch('/api/contact/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        setSnackbar({ open: true, message: t('contact_page.form.success'), severity: 'success' });
        setDialogOpen(false);
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        throw new Error('Error');
      }
    } catch (error) {
      setSnackbar({ open: true, message: t('contact_page.form.error'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: <EmailIcon sx={{ fontSize: 40, color: customTheme.colors.primary[600] }} />,
      title: t('contact_page.contact_info.email'),
      content: 'contact@saloone.fr',
      action: () => window.location.href = 'mailto:contact@saloone.fr',
    },
    {
      icon: <PhoneIcon sx={{ fontSize: 40, color: customTheme.colors.primary[600] }} />,
      title: t('contact_page.contact_info.phone'),
      content: t('contact_page.contact_info.phone_number'),
      action: null,
    },
    {
      icon: <LocationOnIcon sx={{ fontSize: 40, color: customTheme.colors.primary[600] }} />,
      title: t('contact_page.contact_info.address'),
      content: t('contact_page.contact_info.country'),
      action: null,
    },
  ];

  return (
    <Box sx={{ backgroundColor: 'background.default', minHeight: '100vh' }}>
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <GradientHero sx={{ p: { xs: 4, md: 6 } }}>
          <IconButton
            onClick={() => navigate('/')}
            sx={{
              position: 'absolute',
              left: { xs: 8, md: 16 },
              top: { xs: 8, md: 16 },
              color: 'inherit',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.1)' },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h2" component="h1" sx={{ fontWeight: 700, mb: 2 }}>
            {t('contact_page.title')}
          </Typography>
          <Typography variant="h5" sx={{ maxWidth: 700, mx: 'auto', opacity: 0.85 }}>
            {t('contact_page.subtitle')}
          </Typography>
        </GradientHero>

        <Grid container spacing={4} sx={{ mb: 6 }}>
          {contactInfo.map((info, index) => (
            <Grid item xs={12} md={4} key={index}>
              <ContactCard onClick={info.action} sx={{ cursor: info.action ? 'pointer' : 'default' }}>
                <CardContent sx={{ textAlign: 'center', p: 4 }}>
                  <Box sx={{ mb: 2 }}>{info.icon}</Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {info.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {info.content}
                  </Typography>
                </CardContent>
              </ContactCard>
            </Grid>
          ))}
        </Grid>

        <Paper elevation={3} sx={{ p: { xs: 3, md: 5 }, borderRadius: 3, textAlign: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: customTheme.colors.primary[800] }}>
            {t('contact_page.send_message')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
            {t('landing_pro.subtitle', { appName: t('brand.name') })}
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => setDialogOpen(true)}
            startIcon={<SendIcon />}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 3,
              fontSize: '1.1rem',
              fontWeight: 600,
            }}
          >
            {t('contact_page.title')}
          </Button>
        </Paper>
      </Container>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 700, pt: 3 }}>
          {t('contact_page.send_message')}
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 2 }}>
          <Box component="form" noValidate>
            <TextField
              fullWidth
              label={t('contact_page.form.name')}
              margin="normal"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              error={!!errors.name}
              helperText={errors.name}
              required
            />
            <TextField
              fullWidth
              label={t('contact_page.form.email')}
              margin="normal"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              error={!!errors.email}
              helperText={errors.email}
              required
            />
            <TextField
              fullWidth
              label={t('contact_page.form.phone')}
              margin="normal"
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              error={!!errors.phone}
              helperText={errors.phone}
              required
            />
            <TextField
              fullWidth
              label={t('contact_page.form.message')}
              margin="normal"
              multiline
              rows={4}
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              error={!!errors.message}
              helperText={errors.message}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setDialogOpen(false)} color="inherit">
            {t('actions.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={!loading && <SendIcon />}
          >
            {loading ? t('contact_page.form.sending') : t('contact_page.form.send')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
